import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { ChartNoAxesCombined, Check, Pencil, Plus, Trash2, X } from 'lucide-react';
import {
  DashboardDataTable,
  DashboardDataTableColumnKind,
  DashboardPageFormBody,
  buildDashboardDataTableColumns,
  useDashboardDataTableClientPage,
  DashboardTableRowActionItem,
  DashboardTableRowActions,
} from '@shigo-ui/dashboard-ui';
import { Button } from '@shigo-ui/ui';

import { dashboardT } from '../../../../../lib/messages';
import { manageDialogFetchSize } from '../../../../../lib/clientSelectionList';
import { usePlacementIntegrationsQuery } from '../../hooks';
import {
  PlacementApprovalStatus,
  type PlacementDemandIntegration,
  type PlacementLookupFilter,
} from '../../model';
import { normalizePlacementDemandIntegration } from '../../placementFormModel';
import type { PlacementFormValue } from '../../placementFormSchema';
import { PlacementSelectionDialog } from '../PlacementSelectionDialog';
import { PlacementIntegrationEditDialog } from './PlacementIntegrationEditDialog';
import { numberFormat } from '../../../../../lib/format';

const lookupFilter: PlacementLookupFilter = {
  searchTerm: '',
  page: 0,
  size: manageDialogFetchSize,
};

export function PlacementIntegrationsFields() {
  'use no memo';
  const { control, setValue } = useFormContext<PlacementFormValue>();
  const integrations = useWatch({ control, name: 'demandIntegrations' });
  const [manageOpen, setManageOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<PlacementDemandIntegration | null>(null);
  const query = usePlacementIntegrationsQuery(lookupFilter, manageOpen);
  const columns = buildDashboardDataTableColumns<PlacementDemandIntegration>([
    {
      id: 'name',
      header: dashboardT('placements.name'),
      kind: DashboardDataTableColumnKind.Text,
      value: integrationName,
      headerClassName: 'w-72',
      className: 'w-72',
    },
    {
      id: 'duplication',
      header: dashboardT('placements.duplication'),
      kind: DashboardDataTableColumnKind.Custom,
      headerClassName: 'w-28 text-center',
      className: 'w-28 text-center',
      cell: (item) => {
        const duplicated = item.duplicateDemandIntegration ?? item.duplication;
        const label = dashboardT(duplicated ? 'placements.yes' : 'placements.no');
        return duplicated ? (
          <Check className="text-success mx-auto h-4 w-4" aria-label={label} />
        ) : (
          <X className="text-muted-foreground mx-auto h-4 w-4" aria-label={label} />
        );
      },
    },
    {
      id: 'revenuePercentage',
      header: dashboardT('placements.revenuePercentage'),
      kind: DashboardDataTableColumnKind.Text,
      value: (item) => formatPercentage(item.revenuePct ?? item.stats?.global7daysRevenuePct),
      fallback: '-',
      className: 'w-32',
    },
    {
      id: 'sellerDomain',
      header: dashboardT('placements.sellerDomain'),
      kind: DashboardDataTableColumnKind.Text,
      value: integrationSellerDomain,
      fallback: '-',
      className: 'min-w-44',
    },
    {
      id: 'integrationType',
      header: dashboardT('placements.integrationType'),
      kind: DashboardDataTableColumnKind.Text,
      value: (item) => item.integrationType,
      fallback: '-',
      className: 'w-36',
    },
    {
      id: 'drilldown',
      header: dashboardT('placements.drilldown'),
      kind: DashboardDataTableColumnKind.Custom,
      headerClassName: 'w-24 text-center',
      className: 'w-24 text-center',
      cell: (item) => (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          disabled
          aria-label={dashboardT('placements.drilldownFor', { name: integrationName(item) })}
        >
          <ChartNoAxesCombined className="h-4 w-4" aria-hidden="true" />
        </Button>
      ),
    },
    {
      id: 'actions',
      kind: DashboardDataTableColumnKind.Actions,
      cell: (item) => {
        const name = integrationName(item);
        return (
          <DashboardTableRowActions label={dashboardT('placements.actionsFor', { name })}>
            <DashboardTableRowActionItem
              onSelect={() => {
                setEditing(item);
              }}
            >
              <Pencil className="h-4 w-4" aria-hidden="true" />
              {dashboardT('placements.edit')}
            </DashboardTableRowActionItem>
            <DashboardTableRowActionItem
              onSelect={() => {
                setValue(
                  'demandIntegrations',
                  integrations.filter(
                    (candidate) => integrationId(candidate) !== integrationId(item),
                  ),
                  { shouldDirty: true },
                );
              }}
            >
              <Trash2 className="h-4 w-4" aria-hidden="true" />
              {dashboardT('placements.removeIntegration')}
            </DashboardTableRowActionItem>
          </DashboardTableRowActions>
        );
      },
    },
  ]);
  // Long lists render one page of rows, not every row (keeps tab switches fast).
  const tablePage = useDashboardDataTableClientPage(integrations);
  return (
    <DashboardPageFormBody width="lg">
      <DashboardDataTable
        title={dashboardT('placements.integrations')}
        tableLabel={dashboardT('placements.integrations')}
        columns={columns}
        items={tablePage.items}
        pagination={tablePage.pagination}
        getRowKey={integrationId}
        emptyMessage={dashboardT('placements.noSelectedIntegrations')}
        minWidthClassName="min-w-[980px]"
        toolbar={
          <>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setManageOpen(true);
              }}
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              {dashboardT('placements.manageIntegrations')}
            </Button>
            {integrations.length ? (
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  if (window.confirm(dashboardT('placements.clearIntegrationsConfirm')))
                    setValue('demandIntegrations', [], { shouldDirty: true });
                }}
              >
                {dashboardT('placements.clearAll')}
              </Button>
            ) : null}
          </>
        }
      />
      <PlacementSelectionDialog
        open={manageOpen}
        title={dashboardT('placements.manageIntegrations')}
        items={query.data?.content ?? []}
        selected={integrations}
        loading={query.isLoading || query.isFetching}
        error={query.isError}
        getId={integrationId}
        getName={integrationName}
        columns={[
          {
            id: 'status',
            header: dashboardT('placements.status'),
            kind: DashboardDataTableColumnKind.Text,
            value: (item) => item.status ?? item.approvalStatus,
            fallback: '-',
            className: 'w-36',
          },
          {
            id: 'sellerDomain',
            header: dashboardT('placements.sellerDomain'),
            kind: DashboardDataTableColumnKind.Text,
            value: integrationSellerDomain,
            fallback: '-',
            className: 'min-w-44',
          },
          {
            id: 'integrationType',
            header: dashboardT('placements.integrationType'),
            kind: DashboardDataTableColumnKind.Text,
            value: (item) => item.integrationType,
            fallback: '-',
            className: 'w-36',
          },
        ]}
        onOpenChange={setManageOpen}
        onConfirm={(items) => {
          setValue(
            'demandIntegrations',
            items.map((item) => {
              const existing = integrations.find(
                (candidate) => integrationId(candidate) === integrationId(item),
              );
              return normalizePlacementDemandIntegration(
                existing ?? {
                  ...item,
                  approvalStatus: PlacementApprovalStatus.Pending,
                },
              );
            }),
            { shouldDirty: true },
          );
        }}
      />
      {editing ? (
        <PlacementIntegrationEditDialog
          key={integrationId(editing)}
          integration={editing}
          onOpenChange={(open) => {
            if (!open) setEditing(null);
          }}
          onSave={(updated) => {
            setValue(
              'demandIntegrations',
              integrations.map((item) =>
                integrationId(item) === integrationId(updated) ? updated : item,
              ),
              { shouldDirty: true },
            );
            setEditing(null);
          }}
        />
      ) : null}
    </DashboardPageFormBody>
  );
}

function integrationId(item: PlacementDemandIntegration) {
  return item.demandIntegrationId ?? item.id ?? integrationName(item);
}

function integrationSellerDomain(item: PlacementDemandIntegration) {
  return item.schainAsi ?? item.sellerDomain;
}

function formatPercentage(value: number | null | undefined) {
  if (value === null || value === undefined || value === 0) return undefined;
  return numberFormat({ style: 'percent', maximumFractionDigits: 2 }).format(
    Math.abs(value) > 1 ? value / 100 : value,
  );
}

function integrationName(item: PlacementDemandIntegration) {
  return (
    item.demandIntegrationName ??
    item.name ??
    item.dpDisplayName ??
    item.demandIntegrationId ??
    item.id ??
    '-'
  );
}
